import type { EvergladesProductsApiQueryParams, SearchContext } from '@shared/types/types.ts';
import { isProductItem } from '@tilelist/domain/composition/TileListitem.ts';
import type { BlackFridaySliderItem, ProductItem, TileListItem } from '@tilelist/domain/composition/TileListitem.ts';
import { findInsertIndexAfterNthProduct } from '@tilelist/domain/feature-visibility/SDABillboard.ts';

export const BLACK_FRIDAY_SLIDER_POSITION = 12;
export const BLACK_FRIDAY_BANNER_POSITION = 18;
export const BLACK_FRIDAY_SLIDER_MIN_PRODUCTS = 5;
export const COLLAPSED_BLACK_FRIDAY_SLIDER: BlackFridaySliderItem = {
    type: 'BlackFridaySlider',
    state: 'collapsed',
};

const ELIGIBLE_ASSORTMENTS = new Set(['unterhaltungselektronik', 'haushaltsgeräte', 'reinigungsgeräte']);

export function isBlackFridaySliderEligible(searchContext?: SearchContext): boolean {
    const assortment = searchContext?.assortment?.toLowerCase();
    return !!assortment && ELIGIBLE_ASSORTMENTS.has(assortment);
}

/**
 *
 *
 *
 */
export function insertBlackFridaySliderSlot(
    tileListItems: TileListItem[],
    searchContext: SearchContext | undefined,
    currentPage: number,
    active: boolean,
): TileListItem[] {
    if (!active) return tileListItems;
    if (currentPage !== 1 || !isBlackFridaySliderEligible(searchContext)) return tileListItems;

    const insertIndex = findInsertIndexAfterNthProduct(tileListItems, BLACK_FRIDAY_SLIDER_POSITION);
    if (insertIndex >= tileListItems.length && countProducts(tileListItems) < BLACK_FRIDAY_SLIDER_POSITION) {
        return tileListItems;
    }

    const slot: BlackFridaySliderItem = { type: 'BlackFridaySlider', state: 'pending' };
    const hasBanner = tileListItems[insertIndex]?.type === 'PCB';
    const withoutBanner = hasBanner
        ? [...tileListItems.slice(0, insertIndex), ...tileListItems.slice(insertIndex + 1)]
        : tileListItems;
    const withSlot = [...withoutBanner.slice(0, insertIndex), slot, ...withoutBanner.slice(insertIndex)];
    if (!hasBanner) return withSlot;

    const bannerIndex = findInsertIndexAfterNthProduct(withSlot, BLACK_FRIDAY_BANNER_POSITION);
    return [...withSlot.slice(0, bannerIndex), { type: 'PCB' as const }, ...withSlot.slice(bannerIndex)];
}

function countProducts(items: TileListItem[]): number {
    return items.filter(isProductItem).length;
}

/**
 *
 *
 */
export function toBlackFridayQuery(searchContext: SearchContext): EvergladesProductsApiQueryParams {
    const ageGroup = searchContext.topNImplicit?.altersgruppe?.[0];
    const categoryPath = searchContext.categoryPath;
    return {
        rule: searchContext.rule,
        intents: ['blackFriday'],
        ...(ageGroup ? { 'blackFriday.ageGroup': ageGroup } : {}),
        ...(categoryPath ? { 'blackFriday.categoryPath': categoryPath } : {}),
    };
}

/*                                                                                             */
export function toBlackFridaySliderItem(enrichedItems: ProductItem[]): BlackFridaySliderItem {
    const items = enrichedItems.filter((item) => item.variations.length > 0 && !!item.currentVariationId);
    if (items.length < BLACK_FRIDAY_SLIDER_MIN_PRODUCTS) return COLLAPSED_BLACK_FRIDAY_SLIDER;
    return { type: 'BlackFridaySlider', state: 'ready', items };
}
